import * as Sentry from "@sentry/react";

export interface SentryEventContext {
  userId?: number | string | null;
  estateId?: number | string | null;
  [key: string]: unknown;
}

export interface DocumentUploadMetrics {
  fileSize: number;
  fileType: string;
  uploadDurationMs: number;
  extractedDataPoints?: number;
}

export interface UserActionMetrics {
  actionName: string;
  durationMs: number;
  success: boolean;
  errorMessage?: string;
}

function normalizeUnknownToError(error: unknown, fallbackMessage?: string): Error {
  if (error instanceof Error) {
    return error;
  }
  const fallback = fallbackMessage ?? "Unknown error";
  if (typeof error === "string") {
    const t = error.trim();
    return new Error(t.length > 0 ? t : fallback);
  }
  if (error == null) {
    return new Error(fallback);
  }
  const s = String(error);
  return new Error(s === "[object Object]" ? fallback : (fallbackMessage ?? s));
}

export const sentryUtils = {
  captureError: (
    error: unknown,
    fallbackMessage?: string,
    context?: SentryEventContext,
    level: Sentry.SeverityLevel = "error",
  ) => {
    const normalized = normalizeUnknownToError(error, fallbackMessage);
    Sentry.captureException(normalized, {
      level,
      tags: {
        ...(context?.userId != null && { user_id: String(context.userId) }),
        ...(context?.estateId != null && {
          estate_id: String(context.estateId),
        }),
      },
      contexts: {
        app: context,
      },
    });
  },

  captureMessage: (
    message: string,
    level: Sentry.SeverityLevel = "info",
    context?: SentryEventContext,
  ) => {
    Sentry.captureMessage(message, {
      level,
      contexts: {
        app: context,
      },
    });
  },

  logDocumentUpload: (metrics: DocumentUploadMetrics, context?: SentryEventContext) => {
    Sentry.captureMessage("Document uploaded", {
      level: "info",
      tags: {
        event_type: "document_upload",
        file_type: metrics.fileType,
      },
      contexts: {
        document: {
          file_size_bytes: metrics.fileSize,
          file_type: metrics.fileType,
          upload_duration_ms: metrics.uploadDurationMs,
          extracted_data_points: metrics.extractedDataPoints || 0,
        },
        app: context,
      },
    });
  },

  logDocumentValidationError: (
    fileType: string,
    validationErrors: string[],
    context?: SentryEventContext,
  ) => {
    Sentry.captureMessage("Document validation failed", {
      level: "warning",
      tags: {
        event_type: "document_validation_error",
        file_type: fileType,
      },
      contexts: {
        document: {
          file_type: fileType,
          validation_errors: validationErrors,
        },
        app: context,
      },
    });
  },

  logUserAction: (metrics: UserActionMetrics, context?: SentryEventContext) => {
    const level = metrics.success ? "info" : "warning";

    Sentry.captureMessage(`User action: ${metrics.actionName}`, {
      level,
      tags: {
        event_type: "user_action",
        action_name: metrics.actionName,
        success: metrics.success.toString(),
      },
      contexts: {
        user_action: {
          action_name: metrics.actionName,
          duration_ms: metrics.durationMs,
          success: metrics.success,
          error_message: metrics.errorMessage,
        },
        app: context,
      },
    });
  },

  logButtonClick: (buttonName: string, context?: SentryEventContext) => {
    Sentry.addBreadcrumb({
      category: "ui.click",
      message: `Button clicked: ${buttonName}`,
      level: "info",
      data: {
        button_name: buttonName,
        user_id: context?.userId,
        estate_id: context?.estateId,
      },
    });
  },

  logNavigationEvent: (from: string, to: string, context?: SentryEventContext) => {
    Sentry.addBreadcrumb({
      category: "navigation",
      message: `${from} -> ${to}`,
      level: "info",
      data: {
        from_route: from,
        to_route: to,
        user_id: context?.userId,
        estate_id: context?.estateId,
      },
    });
  },

  logDataExtractionResult: (
    documentType: string,
    extractedFields: string[],
    confidenceScores: Record<string, number>,
    context?: SentryEventContext,
  ) => {
    Sentry.captureMessage("Document data extraction completed", {
      level: "info",
      tags: {
        event_type: "data_extraction",
        document_type: documentType,
      },
      contexts: {
        extraction: {
          document_type: documentType,
          extracted_fields: extractedFields,
          average_confidence:
            Object.keys(confidenceScores).length > 0
              ? Object.values(confidenceScores).reduce((a, b) => a + b, 0) /
                Object.keys(confidenceScores).length
              : 0,
          field_confidence: confidenceScores,
        },
        app: context,
      },
    });
  },

  logApiError: (
    endpoint: string,
    statusCode: number,
    errorMessage: string,
    context?: SentryEventContext,
  ) => {
    Sentry.captureMessage(`API Error: ${endpoint}`, {
      level: "error",
      tags: {
        event_type: "api_error",
        endpoint,
        status_code: statusCode.toString(),
      },
      contexts: {
        api: {
          endpoint,
          status_code: statusCode,
          error_message: errorMessage,
        },
        app: context,
      },
    });
  },

  // Only log additional messaging on failure
  logAuthenticationEvent: (
    eventType:
      | "login"
      | "logout"
      | "signup"
      | "oauth_start"
      | "oauth_signup"
      | "otp_verification"
      | "password_reset_requested"
      | "password_reset_completed",
    userId?: string,
  ) => {
    Sentry.captureMessage(`Authentication: ${eventType}`, {
      level: "info",
      tags: {
        event_type: "authentication",
        auth_action: eventType,
        success: false,
      },
      contexts: {
        auth: {
          action: eventType,
          success: false,
          user_id: userId,
        },
      },
    });
  },

  setUserContext: (userId: string, email?: string, estateId?: string) => {
    Sentry.setUser({
      id: userId,
      email,
      ip_address: "{{auto}}",
    });

    Sentry.setContext("estate", {
      estate_id: estateId,
    });
  },

  clearUserContext: () => {
    Sentry.setUser(null);
  },

  startSpan: <T>(name: string, op: string, callback: () => Promise<T>): Promise<T> => {
    return Sentry.startSpan({ name, op }, callback);
  },
};
